import {
  useMemo,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Avatar,
  Badge,
  Cell,
  Column,
  Row,
  TableFrame,
  TableHeader,
  type TableFramePagination,
} from '@skupreme/shared/ui';
import { useAuth } from '@contexts/auth';
import { useMetadata } from '@contexts/org-metadata';
import useUrlPermission from '@hooks/url-permission';
import { brand } from '@utility/config';
import { CommonUtility } from '@utility/common';
import { Tabs } from '@utility/constants/common';
import { flagImages, type ISortObject } from '../../legacy-bridge';
import {
  getProviderIntegrationName,
  IntegrationType,
} from '../../lib/integration';
import { toListEntries } from '../../lib/list-entries';
import { fromSortDescriptor, toSortDescriptor } from '../../lib/list-sort';
import type { StorageProviderListItem } from '../../types';
import { LinkButton } from '../LinkButton';
import { PermissionGate } from '../PermissionGate';
import { ProviderMark } from '../ProviderMark';

type OrgMetadata = { isSummaryView?: boolean };

const COLUMN_COUNT = 5;

/* Counts that open a filtered list read as figures first, links second. */
const COUNT_LINK =
  'sk:text-fg sk:tabular-nums sk:underline sk:decoration-dotted sk:decoration-fg-tertiary sk:underline-offset-4 sk:hover:decoration-fg';

export function CountLink({
  to,
  value,
  onClick,
}: {
  to: string;
  value: number;
  /** Runs on the link's click; it may prevent the link's own navigation and open the page itself. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <Link to={to} onClick={onClick} className={COUNT_LINK}>
      {CommonUtility.numberWithCommas(value)}
    </Link>
  );
}

export function RegionBadge({ code }: { code: string }) {
  const flag = flagImages[code.toUpperCase()];

  return (
    <Badge>
      {flag ? (
        <img
          src={flag}
          alt=""
          className="sk:h-2.5 sk:w-3.5 sk:rounded-xs sk:object-cover"
        />
      ) : null}
      {code.toUpperCase()}
    </Badge>
  );
}

/** The provider column of both lists: its mark, its name (a link when it opens), and the lines under it. */
export function ProviderNameCell({
  logoUrl,
  countryCode,
  name,
  title,
  to,
  onOpen,
  children,
}: {
  logoUrl?: string;
  countryCode?: string;
  name: ReactNode;
  title?: string;
  to?: string;
  /** Runs on the link's click; it may prevent the link's own navigation and open the page itself. */
  onOpen?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children?: ReactNode;
}) {
  return (
    <div className="sk:flex sk:items-center sk:gap-3">
      <ProviderMark logoUrl={logoUrl} countryCode={countryCode} />
      <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-1">
        {to ? (
          <Link
            to={to}
            onClick={onOpen}
            title={title}
            className="sk:flex sk:items-center sk:gap-2 sk:font-medium sk:text-fg sk:no-underline sk:hover:underline"
          >
            {name}
          </Link>
        ) : (
          <span
            title={title}
            className="sk:flex sk:items-center sk:gap-2 sk:font-medium sk:text-fg"
          >
            {name}
          </span>
        )}
        {children}
      </div>
    </div>
  );
}

function OrganizationLine({
  organization,
}: {
  organization: StorageProviderListItem['organization'];
}) {
  if (!organization?.company_name) {
    return null;
  }

  return (
    <span className="sk:flex sk:items-center sk:gap-2 sk:text-fg-secondary">
      <Avatar
        src={organization.logo?.url}
        name={organization.company_name}
        size="xs"
      />
      {organization.company_name}
    </span>
  );
}

function ProviderAction({
  provider,
  disabled,
  onOpen,
}: {
  provider: StorageProviderListItem;
  disabled: boolean;
  onOpen: () => void;
}) {
  const itemId = encodeURIComponent(provider._id);
  const prediction = provider.shipment_predictions?.[0];
  const pending = provider.pending_shipment;

  if (prediction) {
    return (
      <LinkButton
        variant="primary"
        size="sm"
        disabled={disabled}
        onClick={onOpen}
        to={`/app/scm/shipments/create/?origin_id=${
          prediction.origin[0]
        }&destination=${itemId}&type=${encodeURIComponent(
          'Fulfillment Provider',
        )}&step=1`}
      >
        Restock Now
      </LinkButton>
    );
  }

  if (pending?._id) {
    return (
      <LinkButton
        variant="primary"
        size="sm"
        disabled={disabled}
        onClick={onOpen}
        to={`/app/scm/${
          provider.supplier_id
            ? `purchase-orders/create/?supplier_id=${provider.supplier_id}`
            : `shipments/${pending._id}`
        }`}
      >
        Plan Shipment
      </LinkButton>
    );
  }

  return (
    <LinkButton
      size="sm"
      className="sk:whitespace-nowrap"
      disabled={disabled}
      onClick={onOpen}
      to={`/app/scm/shipments/create/?destination=${provider._id}&type=Fulfillment%20Provider`}
    >
      Predictions
    </LinkButton>
  );
}

function ProviderRow({
  provider,
  isNested,
}: {
  provider: StorageProviderListItem;
  isNested?: boolean;
}) {
  const navigate = useNavigate();
  const { isSummaryView } = useMetadata() as OrgMetadata;
  const { switchOrganizationIfDiff } = useAuth();
  const { disabled, hidden } = useUrlPermission();

  const itemId = encodeURIComponent(provider._id);
  const isFBA = provider.integration_type === IntegrationType.spapi;
  const type = getProviderIntegrationName(provider.integration_type);
  const showType = !['manual', 'spapi', IntegrationType.tactical3pl].includes(
    provider.integration_type ?? '',
  );
  const regionCode = isSummaryView
    ? provider.region?.name
    : provider.address?.country || provider.country_code;

  /* A summary account opens a brand's provider inside that brand. */
  const openProvider = () => {
    const organizationId = provider.organization?._id;
    if (isSummaryView && organizationId) {
      switchOrganizationIfDiff(organizationId);
    }
  };

  const countLink = (value: number | undefined, to: string) =>
    value && !isSummaryView ? (
      <CountLink to={to} value={value} onClick={openProvider} />
    ) : (
      value
    );

  const detailsPath = `${itemId}/?tab=${Tabs.storageProvider.details}`;

  return (
    <Row
      id={provider._id}
      isNested={isNested}
      onAction={() => {
        openProvider();
        navigate(detailsPath);
      }}
    >
      <Cell>
        <ProviderNameCell
          logoUrl={provider.wmsOrganizationLogo?.url}
          to={detailsPath}
          onOpen={openProvider}
          name={
            <>
              {provider.name}
              {isFBA ? (
                <img
                  src="/images/marketplaces/fba.svg"
                  alt="FBA"
                  className="sk:w-5"
                />
              ) : null}
            </>
          }
        >
          {(showType && type) || regionCode ? (
            <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-1">
              {showType && type ? <Badge>{type}</Badge> : null}
              {regionCode ? <RegionBadge code={regionCode} /> : null}
            </div>
          ) : null}
          {isSummaryView ? (
            <OrganizationLine organization={provider.organization} />
          ) : null}
        </ProviderNameCell>
      </Cell>
      <Cell>
        {countLink(
          provider.inbound,
          `/app/scm/shipments/?destination=${provider._id}&status=inbound`,
        )}
      </Cell>
      <Cell>
        {countLink(
          provider.completion,
          `/app/scm/shipments/?destination=${provider._id}&status=complete`,
        )}
      </Cell>
      <Cell>
        <CountLink
          to={`${itemId}/?tab=${Tabs.storageProvider.inventory}`}
          value={provider.itemInventory ?? 0}
          onClick={openProvider}
        />
      </Cell>
      <Cell className="sk:text-right">
        {provider.is_integrated ? (
          <PermissionGate disabled={disabled} hidden={hidden} placement="left">
            <ProviderAction
              provider={provider}
              disabled={disabled}
              onOpen={openProvider}
            />
          </PermissionGate>
        ) : (
          <span className="sk:block sk:max-w-60 sk:text-start sk:text-dense sk:text-fg-secondary">
            A {brand.name} representative will reach out to your admin contact
            to initiate the API/FTP integration
          </span>
        )}
      </Cell>
    </Row>
  );
}

type ProvidersTableProps = {
  providers: StorageProviderListItem[];
  loading: boolean;
  sort: ISortObject | undefined;
  onSortChange: (sort: ISortObject) => void;
  pagination: TableFramePagination;
};

export function ProvidersTable({
  providers,
  loading,
  sort,
  onSortChange,
  pagination,
}: ProvidersTableProps) {
  const { isSummaryView } = useMetadata() as OrgMetadata;
  const entries = useMemo(() => toListEntries(providers), [providers]);

  const rows = entries.flatMap<ReactElement>(entry =>
    entry.kind === 'provider'
      ? [<ProviderRow key={entry.provider._id} provider={entry.provider} />]
      : [
          <Row key={entry.id} id={entry.id}>
            <Cell colSpan={COLUMN_COUNT}>
              <div className="sk:flex sk:items-center sk:gap-3">
                <ProviderMark logoUrl="/images/fp/tactical.svg" />
                <span className="sk:font-medium">Tactical Logistics</span>
              </div>
            </Cell>
          </Row>,
          ...entry.providers.map(provider => (
            <ProviderRow key={provider._id} provider={provider} isNested />
          )),
        ],
  );

  return (
    <TableFrame
      aria-label="Storage providers"
      columnCount={COLUMN_COUNT}
      isLoading={loading}
      emptyMessage="No storage providers found"
      sortDescriptor={toSortDescriptor(sort)}
      onSortChange={descriptor =>
        onSortChange(fromSortDescriptor(descriptor, sort))
      }
      pagination={pagination}
      header={
        <TableHeader>
          <Column id="name" isRowHeader allowsSorting>
            {isSummaryView ? 'Storage Provider / Brand' : 'Provider'}
          </Column>
          <Column id="inbound">Inbound</Column>
          <Column id="complete">Completed</Column>
          <Column id="total_items" allowsSorting>
            Units
          </Column>
          <Column id="actions" aria-label="Actions" />
        </TableHeader>
      }
    >
      {rows}
    </TableFrame>
  );
}
